import { Theme, ThemeUICSSObject } from 'theme-ui';
import { responsive, spacing } from '../../../../util';
import { getSelectedColors } from '../../../colors';
import { getSelectionStyles } from './getSelectionStyles';

export const primary: (theme: Theme) => ThemeUICSSObject = (theme) => {
  return {
    variant: 'colors.tables.primary',
    backgroundColor: 'components.primaryTable.body.backgroundColor',
    border: '1px solid',
    borderColor: 'components.primaryTable.borderColor',
    borderCollapse: 'separate',
    borderSpacing: 0,
    tableLayout: 'fixed',
    width: '100%',
    thead: {
      zIndex: 1,
      position: 'sticky',
      insetBlockStart: 0,
      backgroundColor: 'palette.background.paper',

      'tr:not(.filtersRow):not(.responsiveTable__filters__row)': {
        'th, td': {
          variant: 'buttons.primary',
          py: responsive({ 0: 3, 4: 4 }),
          px: responsive({ 0: 3, 4: 4 }),
          width: 'breakWidth',
          backgroundColor: 'palette.primary.main',
          borderColor: 'palette.primary.dark',
          borderRadius: 0,
          borderStyle: 'solid',
          borderWidth: '1px',
          color: 'palette.primary.contrastText',
          display: 'table-cell',
          verticalAlign: 'center',
          wordBreak: 'break-word',
          textAlign: 'left',
          boxShadow: 'none',

          '&.additional-column': {
            width: '50px',
            textAlign: 'center',
            verticalAlign: 'middle',
          },

          '&[role="presentation"]': {
            display: 'none',
          },

          ...theme.palette.getStatesFromPath('components.primaryTable.header'),

          '& *': {
            color: 'inherit',
          },

          '.headButton__label': {
            textAlign: 'left',
            d: '',
          },
          '.headButton__resizer': {
            width: '20px',
            cursor: 'ew-resize',
            zIndex: 1000,
            position: 'absolute',
            right: '-10px',
            height: '100%',
          },
          '&:last-of-type .headButton__resizer': {
            right: '0px',
          },

          '&.additionalColumn': {
            minWidth: '50px',
            maxWidth: '50px',
            width: '50px',
            p: 0,
            textAlign: 'center',
            verticalAlign: 'center',
          },

          '.headButton__container': {
            '.headButton__label ': {
              width: '100%',
            },

            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,

            svg: {
              flexShrink: 0,
            },
          },

          '&:not(:last-of-type)': {
            '&>div': {
              borderRightWidth: '1px',
            },
          },
        },
        'th.requiredFilterColumn': {
          borderLeft: '4px solid !important',
          borderLeftColor: 'palette.error.main !important',
        },
      },
      'tr.filtersRow, tr.responsiveTable__filters__row': {
        backgroundColor: 'components.primaryTable.body.backgroundColor',
        boxShadow: 'tableFilters',

        'th.noFilter': {
          background: 'transparent',
        },
        'td.requiredFilter__Border': {
          borderLeftStyle: 'solid !important',
          borderLeftWidth: '4px !important',
          borderLeftColor: 'palette.error.main',
        },

        '.iconInput': {
          border: 'none',
        },

        td: {
          'input:not([disabled]), select:not([disabled])': {
            border: '1px solid white',
          },
          background: 'transparent',
          border: '1px solid',
          borderColor: 'components.primaryTable.body.borderColor',
          p: 0,
        },

        transition: 'height 0.3s',

        '&.hidden': {
          height: '0',
          overflow: 'hidden',
          p: spacing(0),
          border: 'none',

          '& *': {
            height: '0',
            overflow: 'hidden',
            fontSize: 0,
            lineHeight: 0,
            p: spacing(0),
            border: 'none',
          },
        },
      },
    },
    tbody: {
      tr: {
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        ...theme.palette.getColorsByPath('components.primaryTable.body'),
        '&:nth-of-type(2n)': {
          backgroundColor:
            'components.primaryTable.body.evenRowsBackgroundColor',
        },

        '&.non-selectable': {
          cursor: 'not-allowed',
        },

        '&.hidden': {
          display: 'none',
        },

        ...getSelectedColors(theme.palette),

        '&.draggingRow': {
          outline: '3px dotted',
          outlineColor: 'components.primaryTable.header.borderColor',
          outlineOffset: '-3px',

          '*': {
            outline: 'none !important',
          },
        },

        borderColor: 'components.primaryTable.body.borderColor',

        '.rowStatesList': {
          display: 'flex',
          gap: spacing(3),
          alignItems: 'center',

          svg: {
            width: 'smallIcon',
            height: 'smallIcon',
            cursor: 'pointer',
          },
        },

        td: {
          verticalAlign: 'top',
          wordBreak: 'break-word',

          border: 'none',
          borderBottom: '1px solid',
          background: 'transparent',
          borderColor: 'components.primaryTable.body.borderColor',

          '&.additional-column': {
            width: '50px',
            textAlign: 'center',
            padding: 4,

            button: {
              margin: 'auto',
            },
          },

          '&.stickyColumn': {
            position: 'sticky',
            left: 0,
            backgroundColor: 'components.primaryTable.body.backgroundColor',
          },

          '.moreInformationButton': {
            width: '30px',
            height: '30px',
            padding: '3px',
            border: 'none',
            background: 'transparent',
          },
        },
        '&.locked': {
          ...theme.palette.getOneState(
            { backgroundColor: 'palette.background.paper' },
            'disabled',
          ),
          '&:hover': {
            ...theme.palette.getOneState(
              { backgroundColor: 'palette.background.paper' },
              'disabled',
            ),
          },
          td: {
            borderColor: 'palette.gray.850',
          },
        },
      },
      td: {
        p: spacing(3),
      },

      '.responsiveTable__additionalInfoContainer': {
        display: 'grid',
        gridTemplateColumns: responsive({
          0: 'repeat(2, 1fr)',
          5: 'repeat(4, 1fr)',
        }),
        columnGap: 3,
        rowGap: 2,
      },
      '.responsiveTable__additionalInfoContainer .responsiveTable__additionalInfoItem.separator':
        {
          gridColumnStart: '1',
          gridColumnEnd: '5',
          borderBottom: '1px solid',
          borderBottomColor: 'palette.gray.700',
          height: 'auto',
        },
    },
    '.editionMode': {
      border: '3px solid',
      borderColor: 'components.primaryTable.header.borderColor',
      borderWidth: '1.5px',
      borderStyle: 'dotted',
    },

    '&.editionMode *:focus': {
      outlineWidth: '5px',
      outlineStyle: 'dotted',
    },

    '.stateCell': {
      width: '50px',
      maxWidth: '50px',
      minWidth: '50px',
      textAlign: 'center',
      verticalAlign: 'middle',
    },

    ...getSelectionStyles(),
    '.no__registers': {
      py: 6,
      minWidth: '200px',
    },
    '.requiredFilter__Column': {
      fontWeight: 'bold',
    },

    '.bold': {
      fontWeight: 'bold',
    },

    '.light': {
      color: '#6a6a6a',
    },

    '.additionalInfo__cell, .stateCell': {
      verticalAlign: 'middle',
    },
  };
};
